import {
  Button,
  Checkbox,
  Dropdown,
  MenuProps,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { NotificationTypeEnum } from '../../enums/NotificationTypeEnum';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import TagInterface from '../../interfaces/Tag';
import tagColors from '../../lib/tagColors';
import { Icon } from '../Core/Icon';
import TruncatedTooltip from './TruncatedTooltip';
import { useTags } from '../../hooks/useTags';
import { getContrastBackgroundColorForText } from '../../utils/colorsUtilis';

const TagsList = () => {
  const { t } = useTranslation();
  const { authenticatedUser, isAdmin } = useAuth();
  const { createNotification } = useNotifications();
  const {
    tags,
    isLoadingTags,
    selectedTags,
    setSelectedTags,
    handleOpenTagForm,
    updateTag,
    handleDeleteTag,
  } = useTags();

  /**
   * @description Handle the tag click
   * @param tag TagInterface
   */
  const handleTagClick = (tag: TagInterface) => {
    if (selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags(selectedTags.filter((t) => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  /**
   * @description Get the dropdown items for a tag
   * @param tag TagInterface
   * @returns MenuProps['items']
   */
  const getDropdownItemsForTag = (tag: TagInterface): MenuProps['items'] => {
    let items: MenuProps['items'] = [
      {
        label: t('common.tagColor'),
        icon: <Icon name="SwatchBookIcon" className="h-5 w-5" />,
        key: 'color',
        children: tagColors
          .filter((color) => color.text !== tag.color)
          .map((color) => ({
            label: (
              <Tag className="w-full">
                <div className="flex flex-row items-center gap-2 py-1">
                  <div
                    className="h-4 w-4 rounded-md"
                    style={{
                      backgroundColor: color.text,
                    }}
                  ></div>
                  <span
                    className="text-xs"
                    style={{
                      color: color.text,
                    }}
                  >
                    {t('common.tagColor')}
                  </span>
                </div>
              </Tag>
            ),
            key: color.text,
            onClick: () => {
              updateTag({ ...tag, color: color.text });
            },
          })),
      },
      {
        type: 'divider',
      },
      {
        label: t('common.hideFromList'),
        icon: <Icon name="EyeOffIcon" className="h-5 w-5" />,
        key: 'hide',
        onClick: () => {
          updateTag({ ...tag, fileVisible: false });
        },
      },
      {
        label: t('common.showInList'),
        icon: <Icon name="EyeIcon" className="h-5 w-5" />,
        key: 'show',
        onClick: () => {
          updateTag({ ...tag, fileVisible: true });
        },
      },
      {
        type: 'divider',
      },
      {
        label: t('common.edit'),
        icon: <Icon name="PencilIcon" className="h-5 w-5" />,
        key: 'edit',
        onClick: () => {
          handleOpenTagForm(tag);
        },
      },
      {
        label: t('common.remove'),
        danger: true,
        icon: <Icon name="TrashIcon" className="h-5 w-5" />,
        key: 'remove',
        onClick: () => {
          handleDeleteTag(tag);
        },
      },
    ];

    // Check if the tag is fileVisible and remove the hide and show items
    if (tag.fileVisible) {
      items = items.filter((item) => item?.key !== 'show');
    } else {
      items = items.filter((item) => item?.key !== 'hide');
    }

    return items;
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-center gap-2 pl-3.5 pr-0.5 lg:justify-between">
        <label className="hidden font-medium text-blue-dark lg:block">
          {t('common.tags')}
        </label>
        <div className="mr-0.5 flex w-full flex-row items-center gap-2 lg:w-auto">
          <Tooltip title={t('common.addTag')}>
            <Button
              type="text"
              size="small"
              disabled={isLoadingTags}
              onClick={() => {
                if (tags.length >= 20) {
                  createNotification(
                    t('errors.tagsAmountLimitReached'),
                    NotificationTypeEnum.WARNING
                  );
                } else {
                  handleOpenTagForm(null);
                }
              }}
            >
              <Icon name="AddIcon" className="h-6 w-6 lg:h-4 lg:w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>
      <Spin className="py-10" spinning={isLoadingTags}>
        <div className="flex flex-col">
          {tags?.length === 0 ? (
            <div className="hidden px-4 text-sm text-black lg:block">
              {isLoadingTags ? t('common.loading') : t('common.noTagsYet')}
            </div>
          ) : (
            <div className="flex flex-col">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex w-full cursor-pointer items-center justify-center px-3 lg:justify-between lg:gap-1 lg:px-2.5"
                >
                  <Checkbox
                    className="hidden lg:inline-flex"
                    aria-label={tag.title}
                    checked={selectedTags.some((t) => t.id === tag.id)}
                    onChange={(event) => {
                      if (event.target.checked) {
                        setSelectedTags([...selectedTags, tag]);
                      } else {
                        setSelectedTags(
                          selectedTags.filter((t) => t.id !== tag.id)
                        );
                      }
                    }}
                  />
                  <Button
                    type="text"
                    className="w-full pl-2 pr-0"
                    key={tag.id}
                    disabled={isLoadingTags}
                    onClick={() => handleTagClick(tag)}
                  >
                    <Tag
                      aria-label={tag.title}
                      className={clsx(
                        'relative w-full transition-all duration-300',
                        !tag.fileVisible &&
                          'pointer-events-none absolute left-0 top-0 w-full bg-[repeating-linear-gradient(-45deg,transparent,transparent_5px,#ccc_5px,#ccc_10px)] opacity-50 mix-blend-multiply',
                        // Check if is selected and add the border
                        selectedTags.some((t) => t.id === tag.id)
                          ? 'border-black'
                          : 'border-transparent'
                      )}
                      style={{
                        backgroundColor: getContrastBackgroundColorForText(
                          tag?.color
                        ),
                      }}
                    >
                      <div className="flex w-full flex-row items-center justify-center lg:justify-between">
                        <div />
                        <span
                          className="hidden max-w-28 text-center lg:block"
                          style={{
                            color: tag.color,
                          }}
                        >
                          <TruncatedTooltip>{tag.title}</TruncatedTooltip>
                        </span>
                        <span
                          style={{
                            color: tag.color,
                          }}
                          className="lg:hidden"
                        >
                          <TruncatedTooltip>
                            {tag.title?.slice(0, 1)}
                          </TruncatedTooltip>
                        </span>
                        <div className="hidden lg:block">
                          {tag.publicTag ? (
                            <Icon
                              name="BuildingIcon"
                              className="h-4 w-4 text-gray-dark"
                            />
                          ) : (
                            <div className="w-1" />
                          )}
                        </div>
                      </div>
                    </Tag>
                  </Button>
                  {!tag.publicTag ||
                  (tag.publicTag &&
                    (tag.userId === authenticatedUser?.userId || isAdmin)) ? (
                    <Dropdown
                      menu={{
                        items: getDropdownItemsForTag(tag),
                      }}
                    >
                      <Button type="link" className="p-0 text-black">
                        <Icon name="EllipsisVerticalIcon" className="h-4 w-4" />
                      </Button>
                    </Dropdown>
                  ) : (
                    <div className="w-5" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};

export default TagsList;
